export const ANALYTICS_EVENT_NAMES = [
  "session_start",
  "move_played",
  "hint_used",
  "session_complete",
  "review_graded",
  "course_opened",
  "report_card_shown",
  "free_play_started",
] as const;

export type AnalyticsName = (typeof ANALYTICS_EVENT_NAMES)[number];

export type AnalyticsEvent = {
  name: AnalyticsName;
  at: number;
  props: Record<string, unknown>;
};

export type AnalyticsPort = {
  track(name: AnalyticsName, props?: Record<string, unknown>): void;
  list(): AnalyticsEvent[];
  clear(): void;
  replace(events: AnalyticsEvent[]): void;
};

const STORAGE_KEY = "f15m-analytics";
const EVENT_TYPE = "f15m:analytics";
const MAX_QUEUE = 500;

function readQueue(): AnalyticsEvent[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(events: AnalyticsEvent[]) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events.slice(-MAX_QUEUE)));
  } catch {
    /* ignore quota */
  }
}

export function createMemoryAnalytics(seed: AnalyticsEvent[] = []): AnalyticsPort {
  const events = [...seed];
  return {
    track(name, props = {}) {
      events.push({ name, at: Date.now(), props });
    },
    list() {
      return [...events];
    },
    clear() {
      events.length = 0;
    },
    replace(next) {
      events.length = 0;
      events.push(...next);
    },
  };
}

export function createLocalAnalytics(): AnalyticsPort {
  return {
    track(name, props = {}) {
      const event: AnalyticsEvent = { name, at: Date.now(), props };
      writeQueue([...readQueue(), event]);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(EVENT_TYPE, { detail: event }));
      }
    },
    list() {
      return readQueue();
    },
    clear() {
      writeQueue([]);
    },
    replace(events) {
      writeQueue(events);
    },
  };
}

let defaultPort: AnalyticsPort | null = null;

export function getAnalytics(): AnalyticsPort {
  if (!defaultPort) {
    defaultPort = typeof window === "undefined" ? createMemoryAnalytics() : createLocalAnalytics();
  }
  return defaultPort;
}

export function setAnalyticsForTests(port: AnalyticsPort | null) {
  defaultPort = port;
}

export function track(name: AnalyticsName, props: Record<string, unknown> = {}) {
  getAnalytics().track(name, props);
}
